import React, { useMemo } from "react"
import { useState, useEffect } from "react"
import DataTable from "react-data-table-component"
import { customStyles } from "../Common/DataTableStyles"
import { Button } from "../../../components/ui/button"
import { Badge } from "../../../components/ui/badge"
import { Input } from "../../../components/ui/input"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../../../components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { Search, Plus, Grid, List, Eye, Edit, MoreVertical, Loader2 } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../../components/ui/dropdown-menu"
import { useNavigate, useParams } from "react-router-dom"
import { useUserContext } from "../../../Contexts/UserProvider";
import { useCatalogueManagement } from "../../Services/CatalogueManagement";
import { useHasPermission } from "../../../components/hooks/useHasPermission"
import { toast } from "react-toastify";
import AddRequest from "./AddRequest";
import AddProductDialog from "./AddProductDialog";

const Products = () => {
  const [viewMode, setViewMode] = useState<"grid" | "list">("list")
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [isAddRequestOpen, setIsAddRequestOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updateData, setUpdateData] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [catalogueTypes, setCatalogueTypes] = useState([]);
  const { tenantid, id: tenantCode } = useParams();
  const [partnerTenantName, setPartnerTenantName] = useState<string | null>(null);
  const { tenant } = useUserContext();
  const [typeFilter, setTypeFilter] = useState("all")
  const navigate = useNavigate();

  const canCreateProduct = useHasPermission("ProductManagement.Create");
  const canEditProduct = useHasPermission("ProductManagement.Edit");
  const canCreateOrder = useHasPermission("OrderManagement.Create");

  const { getCatalogueItemsPageWise, getCatalogueTypes } = useCatalogueManagement();
  const [page, setPage] = useState({
    currentPage: 1,
    totalCount: 1,
    totalPages: 1,
    pageNumber: 1,
    pageSize: 10,
    searchTerm: undefined as string | undefined
  });

  useEffect(() => {
    let isMounted = true;
    const delay = setTimeout(() => {
      if (isMounted) {
        handleLoadCatalogue();
      }
    }, 400);

    return () => {
      isMounted = false;
      clearTimeout(delay);
    };
  }, [page?.searchTerm, page?.currentPage, page?.pageSize, tenantid, tenant?.id]);

  useEffect(() => {
    loadCatalogueTypes();
  }, [tenantid, tenant?.id]);

  useEffect(() => {
    if (tenantid) {
      const params = new URLSearchParams(window.location.search);
      const name = decodeURIComponent(params.get("name") || "");
      setPartnerTenantName(name);
    }
  }, [tenantid])

  const openProductView = (product) => {
    navigate(`/app/${tenantCode}/products/detail/${product.id}`, {
      state: {
        product,
        catalogTenantId: tenantid || undefined,
      },
    });
  };

  const openAddProduct = (product) => {
    setIsAddProductOpen(true);
    setUpdateData(product || null);
  };

  const openAddRequest = (product) => {
    setIsAddRequestOpen(true);
    setUpdateData(product || null);
  };

  const closeAddProduct = () => {
    setIsAddProductOpen(false);
    setUpdateData(null);
  };

  const closeAddRequest = () => {
    setUpdateData(null);
    setIsAddRequestOpen(false);
  };

  const handleTablePageChange = (newPage: number) => {
    setPage((prev) => ({
      ...prev,
      currentPage: newPage,
    }));
  };

  const handleTableRowsPerPageChange = (newPageSize: number, newPage: number) => {
    setPage((prev) => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: newPage,
    }));
  };

  const handleLoadCatalogue = () => {
    setIsLoading(true);
    const data = {
      pageNumber: page?.currentPage,
      pageSize: page?.pageSize,
      sortBy: "",
      isAscending: true,
      searchTerm: page?.searchTerm ? page?.searchTerm : '',
      tenantId: tenantid ? tenantid : tenant?.id,
      filters: {
        additionalProp1: "string",
        additionalProp2: "string",
        additionalProp3: "string"
      }
    }

    getCatalogueItemsPageWise(data).then((response) => {
      setCatalogue(response?.items || []);
      setPage((prev) => ({
        ...prev,
        totalCount: response?.totalCount,
        totalPages: response?.totalPages,
        pageNumber: response?.pageNumber,
        pageSize: response?.pageSize,
      }))
      setIsLoading(false);
    }).catch((error) => {
      toast.error("Failed to fetch data");
      console.log(error);
      setIsLoading(false);
    })
  }

  const loadCatalogueTypes = () => {
    getCatalogueTypes(tenantid ? tenantid : tenant?.id).then((response) => {
      setCatalogueTypes(response || []);
    }).catch((error) => {
      console.log("Error", error);
      toast.error("Failed to fetch Product Types");
    });
  }

  const truncateWords = (text: string, wordLimit: number) => {
    if (!text) return "";
    const words = text.split(" ");
    return words?.length > wordLimit
      ? words.slice(0, wordLimit).join(" ") + "..."
      : text;
  };

  const filteredProducts = catalogue?.filter(
    (catalog) =>
      (typeFilter === "all" || catalog.itemCatalogName === typeFilter),
  )

  const columns = useMemo(() => [
    {
      name: "Product",
      cell: (row) => (
        <div className="flex items-center py-2">
          <div className="h-10 w-10 flex-shrink-0 rounded-md bg-gray-800 overflow-hidden relative border border-gray-700">
            <img
              src={row?.imageUrls?.[0] || "/Assets/placeholder.svg"}
              alt={row?.name}
              className="object-cover w-full h-full"
            />
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-white truncate">{truncateWords(row?.name, 5)}</div>
            <div className="text-sm text-gray-400 truncate">{truncateWords(row?.description, 5)}</div>
          </div>
        </div>
      ),
      grow: 2,
      sortable: true,
      selector: (row) => row.name,
    },
    {
      name: "Category",
      cell: (row) => (
        <Badge
          variant="outline"
          className="bg-purple-900/30 text-purple-100 border-purple-800"
        >
          {row?.itemCatalogName}
        </Badge>
      ),
      sortable: true,
      selector: (row) => row.itemCatalogName,
    },
    {
      name: "SKU / Code",
      cell: (row) => (
        <div className="py-2">
          <div className="text-sm text-white">SKU: {row?.sku}</div>
          <div className="text-sm text-gray-400">Code: {row?.code}</div>
        </div>
      ),
      sortable: true,
      selector: (row) => row.sku,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex gap-2 justify-end w-full">
          <Button
            variant="ghost"
            size="sm"
            className="text-purple-400 hover:text-purple-100 hover:bg-purple-900/50"
            onClick={() => openProductView(row)}
          >
            <Eye className="h-4 w-4 mr-1" />
            View
          </Button>
          {canEditProduct && !tenantid && (
            <Button
              variant="ghost"
              size="sm"
              className="text-blue-400 hover:text-blue-100 hover:bg-blue-900/50"
              onClick={() => openAddProduct(row)}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      right: true,
    },
  ], [canEditProduct, tenantid, tenantCode]);

  return (
    <div className="w-full mx-auto p-6 space-y-6 product-list">
      <main className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Product Catalogue {tenantid && partnerTenantName ? `of ${partnerTenantName}` : ""}</h2>
          {!tenantid && canCreateProduct && (
            <Button className="bg-primary hover:bg-primary/90 text-white" onClick={() => openAddProduct(null)}>
              <Plus className="h-4 w-4 mr-2" /> Add Product
            </Button>
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="search"
              placeholder="Search products by name, description, SKU or code..."
              className="pl-10"
              id="search"
              value={page?.searchTerm || ""}
              onChange={(e) => setPage((prev) => ({ ...prev, searchTerm: e.target.value, currentPage: 1 }))}
            />
          </div>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {catalogueTypes?.map((type) => (
                <SelectItem key={type?.id} value={type?.catalogName}>{type?.catalogName}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex gap-2">
            <Button
              variant={viewMode === "grid" ? "default" : "outline"}
              className={viewMode === "grid" ? "bg-primary hover:bg-primary/90" : ""}
              onClick={() => setViewMode("grid")}
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              className={viewMode === "list" ? "bg-primary hover:bg-primary/90" : ""}
              onClick={() => setViewMode("list")}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {viewMode === "grid" ? (
          isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin h-8 w-8 text-gray-400" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredProducts?.length === 0 ? (
                <div className="col-span-full text-center text-gray-500 py-10">
                  No products found
                </div>
              ) : (
                filteredProducts.map((product) => (
                  <Card key={product?.id} className="overflow-hidden hover:shadow-md transition-shadow">
                    <div className="relative aspect-square bg-gray-100">
                      <img
                        src={product?.imageUrls?.[0] || "/Assets/placeholder.svg"}
                        alt={product?.name}
                        className="object-cover w-full h-full"
                      />
                      <div className="absolute top-2 right-2">
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 bg-white/80 rounded-full">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openProductView(product)}>
                              <Eye className="h-4 w-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            {canEditProduct && !tenantid && (
                              <DropdownMenuItem onClick={() => openAddProduct(product)}>
                                <Edit className="h-4 w-4 mr-2" /> Edit
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    <CardHeader className="p-4 pb-0">
                      <CardTitle className="text-lg">{truncateWords(product?.name, 3)}</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-2">
                      <p className="text-sm text-gray-500 line-clamp-2">{truncateWords(product?.description, 4)}</p>
                    </CardContent>
                    <CardFooter className="p-4 pt-0 flex justify-between">
                      {tenantid && canCreateOrder && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-purple-700 hover:text-purple-900"
                          onClick={() => openAddRequest(product)}
                        >
                          New Order
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-purple-700 hover:text-purple-900 ml-auto"
                        onClick={() => openProductView(product)}
                      >
                        View Details
                      </Button>
                    </CardFooter>
                  </Card>
                ))
              )}
            </div>
          )
        ) : (
          <div className="bg-black rounded-lg shadow overflow-hidden border border-gray-800">
            <DataTable
              columns={columns}
              data={filteredProducts}
              keyField="id"
              pagination
              paginationServer
              paginationTotalRows={page.totalCount || 0}
              paginationDefaultPage={page.currentPage}
              paginationPerPage={page.pageSize}
              paginationRowsPerPageOptions={[10, 15, 20, 25]}
              onChangePage={handleTablePageChange}
              onChangeRowsPerPage={handleTableRowsPerPageChange}
              progressPending={isLoading}
              progressComponent={
                <div className="flex items-center justify-center p-10">
                  <Loader2 className="animate-spin h-8 w-8 text-white" />
                </div>
              }
              noDataComponent={
                <div className="text-center py-10 text-gray-400">
                  No products found
                </div>
              }
              customStyles={customStyles}
              highlightOnHover
              pointerOnHover
              responsive
            />
          </div>
        )}
      </main>

      <AddProductDialog
        open={isAddProductOpen}
        onOpenChange={closeAddProduct}
        updateData={updateData}
        isUpdate={updateData?.id ? true : false}
        onSuccessCallBack={handleLoadCatalogue}
      />

      <AddRequest
        isOpen={isAddRequestOpen}
        onClose={closeAddRequest}
        requestData={updateData}
        isUpdate={false}
        onSuccessCallBack={() => { }}
      />
    </div>
  )
}
export default Products;
